import { useCallback, useEffect, useState } from 'react';

const getPermission = () => {
  if (typeof Notification === 'undefined') return 'denied';
  return Notification.permission;
};

export const getWebPushSupportSnapshot = () => {
  if (typeof window === 'undefined') {
    return {
      hydrated: true,
      supported: false,
      permission: 'denied',
      ready: false,
      subscribed: false,
      reason: 'Browser unavailable',
    };
  }

  const hasSW = 'serviceWorker' in navigator;
  const hasPush = 'PushManager' in window;
  const hasNotif = typeof Notification !== 'undefined';
  const secure = window.isSecureContext;
  let reason = '';

  if (!secure) reason = 'Requires HTTPS';
  else if (!hasNotif) reason = 'Notifications API not available';
  else if (!hasPush) reason = 'PushManager not available';
  else if (!hasSW) reason = 'Service Worker not available';

  return {
    hydrated: false,
    supported: Boolean(secure && hasSW && hasPush && hasNotif),
    permission: getPermission(),
    ready: false,
    subscribed: false,
    reason,
  };
};

const getSWRegistrationSafe = async (timeoutMs = 5000) => {
  if (!('serviceWorker' in navigator)) return null;
  const timeout = new Promise((resolve) => setTimeout(() => resolve(null), timeoutMs));
  const ready = (async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      return reg || (await navigator.serviceWorker.getRegistration());
    } catch {
      return navigator.serviceWorker.getRegistration();
    }
  })();
  return (await Promise.race([ready, timeout])) || null;
};

export default function useWebPushStatus({ enabled = true } = {}) {
  const [state, setState] = useState(() => getWebPushSupportSnapshot());

  const refresh = useCallback(async () => {
    const base = getWebPushSupportSnapshot();
    if (!enabled || !base.supported) {
      setState({ ...base, hydrated: true });
      return { ...base, hydrated: true };
    }

    let ready = false;
    let subscribed = false;
    let reason = base.reason;

    try {
      const reg = await getSWRegistrationSafe();
      if (reg) {
        ready = true;
        const sub = await reg.pushManager.getSubscription();
        subscribed = Boolean(sub);
      } else {
        reason = 'Service worker not ready';
      }
    } catch (err) {
      reason = err?.message || 'Service worker not ready';
    }

    const next = {
      ...base,
      hydrated: true,
      permission: getPermission(),
      ready,
      subscribed,
      reason,
    };
    setState(next);
    return next;
  }, [enabled]);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      const next = await refresh();
      if (!mounted) return;
      setState(next);
    };

    run();

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        refresh();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', refresh);

    return () => {
      mounted = false;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', refresh);
    };
  }, [refresh]);

  return { ...state, refresh };
}
